import React, { useEffect } from "react";
import { changeAntdTheme } from "mini-dynamic-antd-theme";

import { useMutation } from "@apollo/client";
import { USER_SETTINGS_QUERY } from "../../../graphql/query/user-settings-query";
import { UPDATE_USER_SETTINGS } from "../../../graphql/mutation/update-user-settings";

import styles from "./user-settings-container.module.css";

import {
  UpdateUserSettingsVariables,
  UpdateUserSettings_updateUserSettings,
} from "../../../graphql/__generated_types__/UpdateUserSettings";
import produce from "immer";
import { UserSettings, UserSettingsType } from "@design-system";
import Form from "antd/lib/form";
import { useUserSettings } from "../../../hooks";
import { GetUserSettings } from "../../../graphql/__generated_types__/GetUserSettings";
import { PRIMARY_COLOUR, isDefaultTheme, HexColour } from "../../../types";

const { ["container"]: containerClassName } = styles as any;

export const UserSettingsContainer = () => {
  const { data } = useUserSettings();

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(data?.getUserSettings);
  }, [form, data]);

  const { darkTheme: darkThemeRaw = false, ...userSettings } = data?.getUserSettings || {
    darkTheme: false,
    customPaletteHex: null,
  };

  const darkTheme = darkThemeRaw ?? false;

  const resetTheme = () => {
    changeAntdTheme(PRIMARY_COLOUR);
  };

  const [updateUserSettings, { loading }] = useMutation<
    UpdateUserSettings_updateUserSettings,
    UpdateUserSettingsVariables
  >(UPDATE_USER_SETTINGS);

  const handleColourSelect = (hex: string) => {
    changeAntdTheme(hex);
  };

  const onFinish = (values: UserSettingsType, customPaletteHex: HexColour) => {
    if (!isDefaultTheme(customPaletteHex)) {
      values.darkTheme = false;
    }
    updateUserSettings({
      variables: {
        data: {
          customPaletteHex: { set: customPaletteHex },
          ...(values.darkTheme && { darkTheme: { set: values.darkTheme } }),
        },
      },
      update: (cache) => {
        let originalDarkTheme: boolean = false;
        cache.updateQuery<GetUserSettings>({ query: USER_SETTINGS_QUERY }, (currentData) => {
          if (currentData) {
            originalDarkTheme = currentData.getUserSettings.darkTheme ?? false;
            return produce(currentData, (draft) => {
              draft.getUserSettings.customPaletteHex = customPaletteHex;
              draft.getUserSettings.darkTheme = values.darkTheme;
            });
          }
        });

        if (originalDarkTheme !== values.darkTheme) {
          window.location.reload();
        }
      },
    });
  };

  return (
    <div className={containerClassName}>
      <h1>User Settings</h1>
      <UserSettings
        data={{
          ...userSettings,
          darkTheme,
          customPaletteHex: userSettings.customPaletteHex as HexColour,
        }}
        onFinish={onFinish}
        resetTheme={resetTheme}
        handleColourSelect={handleColourSelect}
        loading={loading}
      />
    </div>
  );
};
